import React, { useContext, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { faSquareUser } from "@fortawesome/pro-regular-svg-icons";
import { faGear } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConnectedProps, useSelector } from "react-redux";
import { translate } from "../../../config/translator";
import { ClavaContext, ClavaRootContext } from "../../../config/contexts";
import { isRegistered } from "../../../config/utils";
import { pathHome } from "../../../config/routes";
import { connector } from "./redux";
import client from "../../../client";
import { BETA_ENDPOINT, DEV_ENDPOINT, STAG_ENDPOINT } from "../../../config/constants";
import ClavaImage from "../ClavaImage";
import PlayerImage from "../PlayerImage";
import SettingsPanel from "../SettingsPanel";
import SearchDropdown from "../SearchDropdown";
import { selectIsLoggedIn, selectUserValue } from "../../../selectors";
import logoPng from "../../../assets/images/logo-app.png";
import logoPngWhite from "../../../assets/images/logo-app-white.png";

const Header: React.FC<ConnectedProps<typeof connector>> = ({
                                                              changeLanguageAction,
                                                              setEndpoint
                                                            }) => {
  const { l } = useContext(ClavaContext);
  const user = useSelector(selectUserValue);
  const loggedIn = useSelector(selectIsLoggedIn);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const settingsButtonRef = useRef<HTMLButtonElement | null>(null);

  const { theme } = useContext(ClavaRootContext);

  const isAuthenticatedUser = useMemo(
    () => isRegistered(user),
    [user]
  );
  const profileOrLoginPath = isAuthenticatedUser ? "/profile" : "/login";

  const endpoint = useMemo(() => client().getEndpoint(), []);
  const isDev = endpoint === DEV_ENDPOINT;
  const isBeta = endpoint === BETA_ENDPOINT;
  const isStag = endpoint === STAG_ENDPOINT;

  const linkClasses = ({
                         isActive,
                         isPending
                       }: {
    isActive: boolean;
    isPending: boolean;
  }) => {
    if (isActive)
      return "flex flex-row items-center text-primary px-4 py-3 xl:px-4 xl:py-4 rounded-md transition-colors";
    if (isPending)
      return "flex flex-row items-center text-primary px-4 py-3 xl:px-4 xl:py-4 rounded-md transition-colors";
    return "flex flex-row items-center px-4 py-3 hover:text-primary rounded-md transition-colors xl:px-4 xl:py-4";
  };

  const linkClassesStatic =
    "flex flex-row items-center px-4 py-3 border-4 border-transparent hover:text-primary rounded-md transition-colors xl:px-4 xl:py-4";

  return (
    <div
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50
                  bg-bg-secondary dark:bg-bg-secondary-dark/90
                  supports-[backdrop-filter]:backdrop-blur
                  border-b border-light-gray dark:border-light-gray-dark
                  pt-[env(safe-area-inset-top)]
                  ${isBeta ? "!bg-orange" : isStag ? "!bg-green" : isDev ? "!bg-red" : ""}`}
    >
      <div className="w-full mx-auto px-4 max-w-6xl xl:max-w-7xl">
        <div className="grid grid-cols-12 items-center h-24 gap-6">
          <div className="col-span-2 xl:col-span-3">
            <NavLink to={pathHome()} className="flex-shrink-0">
              <img
                className="h-8 md:h-9 lg:h-10 xl:h-10 w-auto flex-shrink-0 object-contain"
                src={theme === "dark" ? logoPng : logoPngWhite}
                alt={translate("emptyString", l)}
                title={translate("emptyString", l)}
              />
            </NavLink>
          </div>

          <SearchDropdown />

          <div className="col-span-3 flex items-center justify-end space-x-2">
            <div className="flex items-center space-x-1">
              <button
                ref={settingsButtonRef}
                type="button"
                onClick={() => setSettingsPanelOpen(!settingsPanelOpen)}
                className={linkClassesStatic}
              >
                <FontAwesomeIcon icon={faGear} className="w-6 h-6" />
              </button>
              <div
                aria-hidden
                className="mx-1 w-px h-8 bg-light-gray dark:bg-light-gray-dark"
              />
            </div>

            {loggedIn ? (
              <div
                className="relative flex items-center min-w-0"
                ref={userMenuRef}
              >
                <NavLink
                  to={profileOrLoginPath}
                  className={`${linkClasses({
                    isActive: false,
                    isPending: false
                  })} items-center`}
                >
                  <div className="rounded-full overflow-hidden">
                    {user?.photo ? (
                      <ClavaImage
                        image={user.photo}
                        width={40}
                      />
                    ) : (
                      <PlayerImage width={40} image={null} round />
                    )}
                  </div>
                </NavLink>
              </div>
            ) : (
              <NavLink
                to="/login"
                className={`${linkClassesStatic} ml-auto mr-8 xl:mr-16`}
              >
                <FontAwesomeIcon icon={faSquareUser} className="w-6 h-6 mr-2" />
                <span className="text-sm font-semibold uppercase text-font dark:text-font-dark">
                  {translate("login", l)}
                </span>
              </NavLink>
            )}
          </div>
        </div>


      </div>
      <SettingsPanel
        isOpen={settingsPanelOpen}
        onClose={() => setSettingsPanelOpen(false)}
        buttonRef={settingsButtonRef}
        changeLanguageAction={changeLanguageAction}
        setEndpoint={setEndpoint}
      />
    </div>
  );
};

export default connector(Header);
